import { useState } from "react"
import { useTranslation } from "react-i18next"
import type { AvoidType, LoadType } from "@/api/adHocCommands"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  availableAvoidOptions,
  availableLoadTypes,
} from "@/components/maps/carRouteOptions"
import { useRouteOptions } from "@/hooks/useRouteOptions"
import { cn } from "@/lib/utils"

const TRIGGER_CLASS =
  "h-7 px-2.5 pr-1.5 text-sm hover:bg-accent dark:hover:bg-accent [&>svg]:ml-0"

type Props = {
  avoidOptions: { label: string; value: AvoidType }[]
  loadTypeOptions: { label: string; value: LoadType }[]
  avoid: AvoidType[]
  loadTypes: LoadType[]
  onAvoidChange: (selected: AvoidType[]) => void
  onLoadTypesChange: (selected: LoadType[]) => void
  onAvoidClose?: (selected: AvoidType[]) => void
  onLoadTypesClose?: (selected: LoadType[]) => void
  updating?: boolean
}

export const RouteOptionPickersView = ({
  avoidOptions,
  loadTypeOptions,
  avoid,
  loadTypes,
  onAvoidChange,
  onLoadTypesChange,
  onAvoidClose,
  onLoadTypesClose,
  updating = false,
}: Props) => {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        "contents",
        updating && "[&>button]:pointer-events-none [&>button]:opacity-70",
      )}
      aria-busy={updating}
    >
      <MultiSelect
        options={loadTypeOptions}
        selected={loadTypes}
        onChange={onLoadTypesChange}
        onClose={onLoadTypesClose}
        placeholder={t("chat.routeOptions.loadType")}
        className={TRIGGER_CLASS}
      />
      <MultiSelect
        options={avoidOptions}
        selected={avoid}
        onChange={onAvoidChange}
        onClose={onAvoidClose}
        placeholder={t("chat.routeOptions.avoid")}
        className={TRIGGER_CLASS}
      />
    </div>
  )
}

export const RouteOptionPickers = ({ driverJid }: { driverJid: string }) => {
  const options = useRouteOptions(driverJid)
  return (
    <RouteOptionPickersView
      avoidOptions={options.avoidOptions}
      loadTypeOptions={options.loadTypeOptions}
      avoid={options.pendingAvoid}
      loadTypes={options.pendingLoadTypes}
      onAvoidChange={options.setPendingAvoid}
      onLoadTypesChange={options.setPendingLoadTypes}
      onAvoidClose={options.applyAvoid}
      onLoadTypesClose={options.applyLoadTypes}
      updating={options.isUpdating}
    />
  )
}

export const RouteOptionPickersPreview = ({
  initialAvoid = [],
  initialLoadTypes = [],
}: {
  initialAvoid?: AvoidType[]
  initialLoadTypes?: LoadType[]
}) => {
  const [avoid, setAvoid] = useState<AvoidType[]>(initialAvoid)
  const [loadTypes, setLoadTypes] = useState<LoadType[]>(initialLoadTypes)
  return (
    <RouteOptionPickersView
      avoidOptions={availableAvoidOptions}
      loadTypeOptions={availableLoadTypes}
      avoid={avoid}
      loadTypes={loadTypes}
      onAvoidChange={setAvoid}
      onLoadTypesChange={setLoadTypes}
    />
  )
}
